/**
 * Unified Trait Landing Page — backs all 7 SEO entity routes:
 *   /families/:slug    /terpenes/:slug   /aromas/:slug
 *   /classifications/:slug  /seeds/:slug  /preservation/:slug
 *   /structures/:slug
 *
 * Single page component, server-driven data shape from /api/traits/{type}/{slug}.
 *
 * Layout order (matters — commercial first):
 *   1. SEO hero (name, label, counts, CTA)
 *   2. Available CannaForge listings (only if listings > 0)
 *   3. Matching Lineage Atlas strains
 *   4. Educational copy (LLM-cached, careful)
 *   5. Related families/traits + breeders
 */
import React, { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import {
  BadgeCheck, Sparkles, ShoppingBag, ArrowRight, GitBranch,
  Beaker, Wind, Layers, Sprout, Mountain, Hexagon, ExternalLink, BookOpen,
} from "lucide-react";
import { api } from "../lib/apiClient";
import { useSeo } from "../lib/useSeo";
import { useAuth } from "../context/AuthContext";
import ToolGateway from "../components/ToolGateway";
import ShareButton from "../components/ShareButton";

const TYPE_ICON = {
  families: GitBranch, terpenes: Beaker, aromas: Wind,
  classifications: Layers, seeds: Sprout, preservation: Mountain,
  structures: Hexagon,
};

const TYPE_HUMAN = {
  families: "Family", terpenes: "Terpene", aromas: "Aroma",
  classifications: "Classification", seeds: "Seed type",
  preservation: "Preservation", structures: "Plant structure",
};

const PROD_ORIGIN = "https://thecannaforge.com";

export default function TraitLandingPage({ traitType }) {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    setData(null);
    setNotFound(false);
    api.get(`/traits/${traitType}/${slug}`)
      .then((r) => setData(r.data))
      .catch((err) => {
        if (err?.response?.status === 404) setNotFound(true);
        else setData(null);
      });
  }, [traitType, slug]);

  useSeo({
    title: data?.seo?.title || `${TYPE_HUMAN[traitType] || "Trait"} · CannaForge`,
    description: data?.seo?.description ||
      `Cannabis genetics tagged with ${slug} on CannaForge.`,
    canonical: data ? `${PROD_ORIGIN}${data.canonical}` : `${PROD_ORIGIN}/${traitType}/${slug}`,
    noindex: data ? !data.indexable : false,
    noindexFollow: true,
    jsonLd: data ? buildJsonLd(data) : null,
  });

  if (notFound) {
    const fallback = traitType === "families" ? "/families" : "/lineage";
    return <Navigate to={fallback} replace />;
  }
  if (!data) {
    return (
      <div className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="text-xs font-mono opacity-60" data-testid="trait-page-loading">Loading…</div>
      </div>
    );
  }

  const Icon = TYPE_ICON[traitType] || Hexagon;
  const hasListings = (data.listings || []).length > 0;

  return (
    <div data-testid={`trait-landing-${traitType}-${data.slug}`}>
      {/* HERO */}
      <section className="border-b border-[var(--border)] bg-[var(--bg)]">
        <div className="max-w-[1400px] mx-auto px-6 py-12 lg:py-20">
          <Breadcrumb traitType={traitType} name={data.name} />
          <div className="label-eyebrow mt-4 inline-flex items-center gap-1.5"
               style={{ color: "var(--gold)" }}>
            <Icon className="w-3 h-3" /> {data.label} ·{" "}
            {data.counts.strains} strain{data.counts.strains === 1 ? "" : "s"}
            {data.counts.listings > 0 && (
              <span className="ml-2 px-1.5 py-0.5 text-[10px]"
                    style={{ background: "var(--gold)", color: "#000" }}
                    data-testid="trait-hero-listing-badge">
                {data.counts.listings} live · shop available
              </span>
            )}
            {!data.indexable && (
              <span className="ml-2 text-[10px] opacity-60">noindexed</span>
            )}
          </div>
          <h1 className="font-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.92] mt-3"
              style={{ color: "var(--text)" }} data-testid="trait-hero-title">
            {data.name}
          </h1>
          {data.edu?.intro && (
            <p className="mt-5 max-w-3xl text-base md:text-lg leading-relaxed"
               style={{ color: "var(--text-2)" }}
               data-testid="trait-hero-intro">
              {data.edu.intro}
            </p>
          )}
          {hasListings && (
            <div className="mt-6 flex flex-wrap gap-2 items-center">
              <a href="#available"
                 className="px-4 py-2 inline-flex items-center gap-2 text-xs font-mono uppercase tracking-[0.16em]"
                 style={{ background: "var(--gold)", color: "#000", borderRadius: 2 }}
                 data-testid="trait-hero-shop-cta">
                <ShoppingBag className="w-3.5 h-3.5" />
                Shop {data.counts.listings} matching listing{data.counts.listings === 1 ? "" : "s"}
                <ArrowRight className="w-3.5 h-3.5" />
              </a>
              {/* v3.99c.69 — Share */}
              <ShareButton
                title={data.seo?.title || `${data.name} · CannaForge`}
                description={data.edu?.intro || data.seo?.description || ""}
                path={data.canonical || `/${traitType}/${data.slug}`}
                size="sm"
                testid="trait-hero-share"
              />
            </div>
          )}
          {!hasListings && (
            <div className="mt-6">
              <ShareButton
                title={data.seo?.title || `${data.name} · CannaForge`}
                description={data.edu?.intro || data.seo?.description || ""}
                path={data.canonical || `/${traitType}/${data.slug}`}
                size="sm"
                testid="trait-hero-share"
              />
            </div>
          )}
        </div>
      </section>

      {/* COMMERCIAL — listings first */}
      {hasListings && (
        <section id="available" className="border-b border-[var(--border)] bg-[var(--surface)]">
          <div className="max-w-[1400px] mx-auto px-6 py-12">
            <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
              <div>
                <div className="label-eyebrow mb-1" style={{ color: "var(--gold)" }}>Available on CannaForge</div>
                <h2 className="font-display text-2xl md:text-3xl tracking-tighter"
                    style={{ color: "var(--text)" }}>
                  Shop {data.name} genetics
                </h2>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3"
                 data-testid="trait-listings-grid">
              {data.listings.map((l) => (
                <Link key={l.id} to={l.url}
                      className="border border-[var(--border)] bg-[var(--bg)] hover:border-[var(--gold)]/60 transition-colors p-3 block"
                      data-testid={`trait-listing-${l.id}`}>
                  {l.image && (
                    <div className="aspect-square bg-[var(--surface-2)] mb-2 overflow-hidden">
                      <img src={l.image} alt={l.title}
                           className="w-full h-full object-cover"
                           loading="lazy" />
                    </div>
                  )}
                  <div className="text-xs font-mono uppercase tracking-[0.14em] opacity-60 mb-1"
                       style={{ color: "var(--text-muted)" }}>
                    {l.store_name || "CannaForge"}
                  </div>
                  <div className="text-sm font-semibold truncate" style={{ color: "var(--text)" }}>
                    {l.title}
                  </div>
                  <div className="mt-1.5 flex items-center justify-between text-[11px] font-mono"
                       style={{ color: "var(--text-muted)" }}>
                    <span>{l.seed_count ? `${l.seed_count}-pack` : (l.seed_type || "—")}</span>
                    <span style={{ color: "var(--gold)" }}>
                      {l.listing_type === "auction" && l.starting_bid != null
                        ? `From $${l.starting_bid}`
                        : l.price != null ? `$${l.price}` : "—"}
                    </span>
                  </div>
                  {l.featured && (
                    <div className="mt-1 text-[9px] font-mono uppercase tracking-[0.14em]"
                         style={{ color: "var(--gold)" }}>★ featured</div>
                  )}
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* STRAINS — lineage atlas matches */}
      <section className="max-w-[1400px] mx-auto px-6 py-12">
        <div className="flex items-end justify-between mb-5 flex-wrap gap-2">
          <div>
            <div className="label-eyebrow mb-1" style={{ color: "var(--gold)" }}>
              Lineage Atlas · {data.counts.strains} record{data.counts.strains === 1 ? "" : "s"}
            </div>
            <h2 className="font-display text-2xl md:text-3xl tracking-tighter"
                style={{ color: "var(--text)" }}>
              {data.name} strains
            </h2>
          </div>
          {data.counts.strains > 0 && (
            <Link to={`/lineage?${queryFor(traitType, data)}`}
                  className="text-xs font-mono uppercase tracking-[0.18em] inline-flex items-center gap-1 hover:opacity-100 opacity-80"
                  style={{ color: "var(--gold)" }}
                  data-testid="trait-strains-see-all">
              Open in Lineage Atlas <ArrowRight className="w-3 h-3" />
            </Link>
          )}
        </div>

        {data.strains.length === 0 ? (
          <EmptyStrainsCard name={data.name} traitType={traitType} />
        ) : (
          <TraitStrainsGated strains={data.strains} traitName={data.name} traitType={traitType} />
        )}
      </section>

      {/* EDUCATIONAL */}
      {(data.edu?.intro || data.edu?.breeding_relevance) && (
        <section className="border-t border-[var(--border)] bg-[var(--surface)]">
          <div className="max-w-[1400px] mx-auto px-6 py-12">
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <div className="label-eyebrow mb-2" style={{ color: "var(--gold)" }}>
                  About {data.name}
                </div>
                <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--text-2)" }}
                   data-testid="trait-edu-intro">
                  {data.edu.intro}
                </p>
              </div>
              {data.edu.breeding_relevance && (
                <div>
                  <div className="label-eyebrow mb-2" style={{ color: "var(--gold)" }}>
                    Breeder relevance
                  </div>
                  <p className="text-sm md:text-base leading-relaxed" style={{ color: "var(--text-2)" }}
                     data-testid="trait-edu-breeding">
                    {data.edu.breeding_relevance}
                  </p>
                </div>
              )}
            </div>
            <p className="mt-6 text-[11px] font-mono uppercase tracking-[0.14em] opacity-60"
               style={{ color: "var(--text-muted)" }}>
              Educational reference · Cultivar metadata only · No medical claims
            </p>
            <div className="mt-4 flex flex-wrap gap-3 text-[11px] font-mono uppercase tracking-[0.14em]"
                 data-testid="trait-edu-cross-links">
              <Link to="/genetics-guide" className="inline-flex items-center gap-1 underline hover:text-[var(--gold)]"
                    style={{ color: "var(--text-2)" }}
                    data-testid="trait-edu-link-genetics-guide">
                <BookOpen className="w-3 h-3" /> Read the Genetics Guide
              </Link>
              <Link to="/grower-resources" className="inline-flex items-center gap-1 underline hover:text-[var(--gold)]"
                    style={{ color: "var(--text-2)" }}
                    data-testid="trait-edu-link-grower-resources">
                <Sprout className="w-3 h-3" /> Grower Resources
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* RELATED + BREEDERS + CTA */}
      <section className="border-t border-[var(--border)]">
        <div className="max-w-[1400px] mx-auto px-6 py-12 grid md:grid-cols-2 lg:grid-cols-3 gap-8">
          {(data.related || []).length > 0 && (
            <div>
              <div className="label-eyebrow mb-3" style={{ color: "var(--gold)" }}>Related traits</div>
              <div className="flex flex-wrap gap-2" data-testid="trait-related">
                {data.related.map((r, idx) => (
                  <Link key={`${r.type}-${r.slug}-${idx}`}
                        to={`/${r.type}/${r.slug}`}
                        className="text-xs px-3 py-1.5 border border-[var(--border)] hover:border-[var(--gold)]/50 transition-colors"
                        style={{ color: "var(--text)" }}
                        data-testid={`trait-related-${r.type}-${r.slug}`}>
                    {r.name}
                    {r.count != null && (
                      <span className="ml-1.5 opacity-60">({r.count})</span>
                    )}
                  </Link>
                ))}
              </div>
            </div>
          )}

          {(data.breeders || []).length > 0 && (
            <div>
              <div className="label-eyebrow mb-3" style={{ color: "var(--gold)" }}>
                Breeders attributed
              </div>
              <div className="flex flex-wrap gap-2" data-testid="trait-breeders">
                {data.breeders.map((b) => (
                  <Link key={b.id} to={`/breeder/${b.store_slug || b.id}`}
                        className="border border-[var(--border)] hover:border-[var(--gold)]/60 transition-colors px-3 py-2 inline-flex items-center gap-2"
                        data-testid={`trait-breeder-${b.id}`}>
                    {b.avatar_url && (
                      <img src={b.avatar_url} alt="" className="w-5 h-5 rounded-full object-cover"
                           loading="lazy" />
                    )}
                    <span className="text-xs font-semibold" style={{ color: "var(--text)" }}>
                      {b.store_name}
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Breeder + collector CTA */}
          <div>
            <div className="label-eyebrow mb-3" style={{ color: "var(--gold)" }}>
              For breeders & collectors
            </div>
            <div className="space-y-2">
              <Link to="/join/breeder"
                    className="block border border-[var(--border)] hover:border-[var(--gold)]/60 transition-colors p-3"
                    data-testid="trait-cta-breeder">
                <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                  List genetics in this {data.label.toLowerCase()}
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                  Apply as a breeder &middot; managed onboarding
                </div>
              </Link>
              <Link to="/collector-plus"
                    className="block border border-[var(--border)] hover:border-[var(--gold)]/60 transition-colors p-3"
                    data-testid="trait-cta-collector">
                <div className="text-sm font-semibold" style={{ color: "var(--text)" }}>
                  Follow {data.name} drops
                </div>
                <div className="text-[11px] mt-0.5" style={{ color: "var(--text-muted)" }}>
                  Get notified when new {data.name} genetics list
                </div>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}

function Breadcrumb({ traitType, name }) {
  const label = TYPE_HUMAN[traitType] || "Trait";
  const indexLabel = (
    traitType === "families"        ? "Families" :
    traitType === "terpenes"        ? "Terpenes" :
    traitType === "aromas"          ? "Aromas" :
    traitType === "classifications" ? "Classifications" :
    traitType === "seeds"           ? "Seed types" :
    traitType === "preservation"    ? "Preservation" :
    traitType === "structures"      ? "Structures" : `${label}s`
  );
  const indexPath =
    traitType === "families" ? "/families" :
    traitType === "classifications" ? "/lineage" :
    "/lineage";
  return (
    <nav aria-label="Breadcrumb" data-testid="trait-breadcrumb">
      <ol className="flex flex-wrap items-center gap-1.5 text-[11px] font-mono uppercase tracking-[0.16em]"
          style={{ color: "var(--text-muted)" }}>
        <li><Link to="/" className="hover:text-[var(--gold)]">CannaForge</Link></li>
        <li>/</li>
        <li><Link to={indexPath} className="hover:text-[var(--gold)]">{indexLabel}</Link></li>
        <li>/</li>
        <li style={{ color: "var(--text)" }}>{name}</li>
      </ol>
    </nav>
  );
}

// v3.99c.67 — Public/gated trait strain grid. Logged-out visitors see the
// first 8 strains (canonical, listing-connected, or verified come first
// when the backend orders them that way) and then a ToolGateway card.
// Remaining strains stay in the DOM under .cf-locked so crawlers index
// them. Logged-in users see everything inline.
function TraitStrainsGated({ strains, traitName, traitType }) {
  const { user } = useAuth();
  const PUBLIC_LIMIT = 8;
  const visible = user ? strains : strains.slice(0, PUBLIC_LIMIT);
  const hidden = user ? [] : strains.slice(PUBLIC_LIMIT);
  return (
    <>
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2"
           data-testid="trait-strains-grid">
        {visible.map((s) => (
          <TraitStrainCard key={s.id} s={s} />
        ))}
      </div>
      {hidden.length > 0 && (
        <>
          <ToolGateway
            testid="trait-strains-gateway"
            eyebrow={`Member tool · ${TYPE_HUMAN[traitType] || "Trait"}`}
            title={`See all ${strains.length} ${traitName} strains`}
            description={`Create a free CannaForge account to unlock the full list, advanced filters, breeder-connected genetics, and the interactive Genetics Map view for ${traitName}.`}
          />
          {/* SEO surface: the rest of the strains stay in the DOM (hidden
              by .cf-locked) so crawlers can still index them. */}
          <div aria-hidden="true" className="cf-locked" data-testid="trait-strains-locked-content">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
              {hidden.map((s) => (
                <TraitStrainCard key={s.id} s={s} />
              ))}
            </div>
          </div>
        </>
      )}
    </>
  );
}

function TraitStrainCard({ s }) {
  return (
    <Link to={`/strains/${s.slug}`}
          className="border border-[var(--border)] bg-[var(--surface)] hover:border-[var(--gold)]/50 transition-colors px-3 py-2.5"
          data-testid={`trait-strain-${s.id}`}>
      <div className="flex items-start justify-between gap-1">
        <div className="text-xs font-semibold truncate flex-1" style={{ color: "var(--text)" }}>
          {s.strain_name}
        </div>
        {s.priority_canonical && (
          <Sparkles className="w-3 h-3 flex-shrink-0" style={{ color: "var(--gold)" }} />
        )}
        {s.verification_status === "verified" && !s.priority_canonical && (
          <BadgeCheck className="w-3 h-3 flex-shrink-0 opacity-70" style={{ color: "var(--gold)" }} />
        )}
      </div>
      {(s.parent_one || s.parent_two) && (
        <div className="text-[10px] truncate mt-0.5" style={{ color: "var(--text-muted)" }}>
          {s.parent_one || "?"} × {s.parent_two || "?"}
        </div>
      )}
      {s.has_listing && (
        <div className="text-[9px] font-mono uppercase tracking-[0.14em] mt-1"
             style={{ color: "var(--gold)" }}>● live</div>
      )}
    </Link>
  );
}


function EmptyStrainsCard({ name, traitType }) {
  return (
    <div className="border border-[var(--border)] bg-[var(--surface)] p-8 text-center"
         data-testid="trait-strains-empty">
      <p className="text-sm" style={{ color: "var(--text-muted)" }}>
        No strains tagged into <strong style={{ color: "var(--text)" }}>{name}</strong> yet —
        they'll appear here as breeders submit lineage records under this {TYPE_HUMAN[traitType]?.toLowerCase() || "trait"}.
      </p>
      <div className="mt-4 flex flex-wrap items-center justify-center gap-2 text-[11px] font-mono uppercase tracking-[0.16em]">
        <Link to="/lineage" className="underline hover:text-[var(--gold)]"
              style={{ color: "var(--text-2)" }}
              data-testid="trait-empty-link-lineage">
          Browse Lineage Atlas
        </Link>
        <span style={{ color: "var(--text-muted)" }}>·</span>
        <Link to="/families" className="underline hover:text-[var(--gold)]"
              style={{ color: "var(--text-2)" }}
              data-testid="trait-empty-link-families">
          Genetic families
        </Link>
        <span style={{ color: "var(--text-muted)" }}>·</span>
        <Link to="/breeders" className="underline hover:text-[var(--gold)]"
              style={{ color: "var(--text-2)" }}
              data-testid="trait-empty-link-breeders">
          Verified breeders
        </Link>
      </div>
    </div>
  );
}

function queryFor(traitType, data) {
  if (traitType === "families") return `family=${encodeURIComponent(data.name)}`;
  if (traitType === "classifications") return `classification=${encodeURIComponent(data.slug)}`;
  return "";
}

function buildJsonLd(data) {
  const url = `${PROD_ORIGIN}${data.canonical}`;
  const items = (data.strains || []).slice(0, 30).map((s, i) => ({
    "@type": "ListItem",
    "position": i + 1,
    "url": `${PROD_ORIGIN}/strains/${s.slug || s.id}`,
    "name": s.strain_name,
  }));
  const products = (data.listings || []).slice(0, 20).map((l) => ({
    "@context": "https://schema.org",
    "@type": "Product",
    "name": l.title,
    "url": `${PROD_ORIGIN}${l.url}`,
    ...(l.price != null && {
      "offers": { "@type": "Offer", "priceCurrency": "USD", "price": l.price,
                  "availability": "https://schema.org/InStock" },
    }),
  }));
  // Emit each entity as its own top-level @type — easier for crawlers and
  // testers to extract than a nested @graph (and avoids the "null"-block
  // parse failure observed when wrapping everything in a single graph).
  return [
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      "itemListElement": [
        { "@type": "ListItem", "position": 1, "name": "Home",       "item": `${PROD_ORIGIN}/` },
        { "@type": "ListItem", "position": 2, "name": data.label,   "item": `${PROD_ORIGIN}/${data.trait_type}` },
        { "@type": "ListItem", "position": 3, "name": data.name,    "item": url },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "DefinedTerm",
      "name": data.name,
      "description": data.edu?.intro || data.seo.description,
      "url": url,
      "inDefinedTermSet": `${PROD_ORIGIN}/${data.trait_type}`,
    },
    {
      "@context": "https://schema.org",
      "@type": "CollectionPage",
      "name": data.seo.title,
      "url": url,
      "description": data.seo.description,
      "mainEntity": { "@type": "ItemList", "itemListElement": items },
    },
    ...products,
  ];
}
